import { Colord, RgbColor, RgbaColor, colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import mixPlugin from 'colord/plugins/mix';
import namesPlugin from 'colord/plugins/names';

extend([namesPlugin, mixPlugin, a11yPlugin]);

type Color = string;
type RGBAParsed = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export const toRgbString = (color: Color) => colord(color).toRgbString();
export const getAlpha = (color: Color) => colord(color).alpha();
export const getColorWithAlphaString = (color: Color, alpha: number) => colord(color).alpha(alpha).toRgbString();
export const getColorWithAlphaStringHex = (color: Color, alpha: number) => colord(color).alpha(alpha).toHex();
export const removeHexAlpha = (color: Color) => colord(color).alpha(1).toHex();
export const removeAlpha = (color: Color) => colord(color).alpha(1).toRgbString();
export const parseColorToRgba = (color: Color): RGBAParsed => colord(color).rgba;
export const toHex = (color: Color) => colord(color).toHex();
export const isTransparent = (color: Color) => colord(color).alpha() === 0;
export const isLight = (color: Color) => colord(color).isLight();
export const isDark = (color: Color) => colord(color).isDark();
export const getLuminance = (color: Color) => colord(color).luminance();

/**
 * Mixes all colours together and returns the resulting colour
 *
 * @param colors Array of colors to mix
 * @returns New colour with all colours mixed together
 */
export const mixColors = (colors: string[]) => {
  let mixedColor = colord(colors[0]);
  for (let i = 1; i < colors.length; i++) {
    mixedColor = mixedColor.mix(colors[i], 1 / (i + 1));
  }
  return mixedColor.toHex();
};

/*
 * Returns the contrast ratio between two colors
 *
 * @param bgColor Background color
 * @param fgColor Foreground color
 * @returns Contrast ratio between the two colors
 */
export const contrast = (bgColor: Color, fgColor: Color) => colord(fgColor).contrast(bgColor);

/**
 * Returns a color with the same alpha as the old color
 *
 * @param oldColor old color
 * @param newColor new color with same alpha as old color
 */
export const maintainAlpha = (oldColor: Color, newColor: Color) => {
  const alpha = getAlpha(oldColor);
  return getColorWithAlphaString(newColor, alpha);
};

/**
 * Generate a palette with tints and shades of `baseColor`
 *
 * @param baseColor
 * @returns array of hex colour strings
 */
export const generatePaletteFromColor = (baseColor: Color) => [
  ...new Set([
    ...colord(baseColor)
      .tints()
      .map((c) => c.toHex().toUpperCase()),
    ...colord(baseColor)
      .shades()
      .map((c) => c.toHex().toUpperCase()),
  ]),
];

/**
 * Determines the outline color based on the luminance of a given color.
 *
 * @param {string} color - The color to evaluate. This can be in either RGB or HEX format.
 * @returns {string} The contrasting outline color ('#000000' for light colors, '#ffffff' for dark colors).
 */
export const getOutlineColorBasedOnLuminance = (color: string): Color =>
  getLuminance(color) > 0.5 ? '#000000' : '#ffffff';

// All Color utility functions below are credited to E1

/**
 * Return the hover color to make it more visible
 * @param color {string} color in any format that colord prodives
 * @returns {string} color in hex format, if invalid color is provided, return the given value
 */
export const adjustHoverColor = (color: string) => {
  const parsedColor: Colord = colord(color);
  if (color === '' || !colord(color).isValid()) return color;

  const { r, g, b } = parsedColor.toRgb();

  const luminance = getLuminance(color);
  const amount = luminance > 0.5 ? -40 : 40; // Make it dark when it's bright, make it bright when it's dark

  const newR = Math.min(Math.max(0, r + amount), 255);
  const newG = Math.min(Math.max(0, g + amount), 255);
  const newB = Math.min(Math.max(0, b + amount), 255);

  return colord({ r: newR, g: newG, b: newB }).toHex();
};

/**
 * Convert HSV to RGB value
 * @param {number} h , 0 <= value <= 1
 * @param {number} s , 0 <= value <= 1
 * @param {number} v , 0 <= value <= 1
 * @returns {object} rgb , 0 <= value <= 255
 */
export function VgHSVtoRGB(h: number, s: number, v: number) {
  let r: number = 0,
    g: number = 0,
    b: number = 0,
    i,
    f,
    p,
    q,
    t;
  i = Math.floor(h * 6);
  f = h * 6 - i;
  p = v * (1 - s);
  q = v * (1 - f * s);
  t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Convert aValue on range [rangeMinA, rangeMaxA] to what it'd be on [rangeMinB, rangeMaxB]
 * with same percentage. For e.x: if aValue is 50% in rangeA, then return what 50% is on rangeB.
 * @param {number} rangeMinA
 * @param {number} rangeMaxA
 * @param {number} rangeMinB
 * @param {number} rangeMaxB
 * @param {number} aValue
 */
export function getValueOnDifferentRangeWithSamePercentage(
  valueOnRangeA: number,
  rangeMinA: number,
  rangeMaxA: number,
  rangeMinB: number,
  rangeMaxB: number,
) {
  const percentageOnRangeA = (valueOnRangeA - rangeMinA) / (rangeMaxA - rangeMinA);
  const valueOnRangeB = (rangeMaxB - rangeMinB) * percentageOnRangeA + rangeMinB;
  return valueOnRangeB;
}

/**
 * Convert tintSlider value to rgb color
 * @param {*} tintValue from slider
 * @param {*} intensity of tint as percentage
 * @returns {object} rgb e.x: {r:2, g:2, b:2}
 */
export function convertTintToFeFuncRgbIntercept(tintValue: number, intensity = 50) {
  if (!tintValue) return null;
  // TODO: move these to shared lib constants file.
  // can't do right now because core is not setup to use @venngage packages.
  const MAX_TINT_SLIDER_VALUE = 100;
  const MIN_TINT_SLIDER_VALUE = -100;
  const MAX_FEFUNC_RGB_INTERCEPT = (0.3 * intensity) / 100;
  const MIN_FEFUNC_RGB_INTERCEPT = 0.0;
  const MAX_HUE_DECIMAL = 1.0;
  const MIN_HUE_DECIMAL = 0.0;
  const MIN_RGB = 0;
  const MAX_RGB = 255;
  // convert slider[-100<0<100] to hueDecimal[0<0.5<1.0]
  let hueInDecimal = getValueOnDifferentRangeWithSamePercentage(
    tintValue,
    MIN_TINT_SLIDER_VALUE,
    MAX_TINT_SLIDER_VALUE,
    MIN_HUE_DECIMAL,
    MAX_HUE_DECIMAL,
  );
  const rgb = VgHSVtoRGB(hueInDecimal, 1.0, 1.0); // max out saturation and value to only look at hue.
  const feFuncRgbIntercept = {
    r: getValueOnDifferentRangeWithSamePercentage(
      rgb.r,
      MIN_RGB,
      MAX_RGB,
      MIN_FEFUNC_RGB_INTERCEPT,
      MAX_FEFUNC_RGB_INTERCEPT,
    ),
    g: getValueOnDifferentRangeWithSamePercentage(
      rgb.g,
      MIN_RGB,
      MAX_RGB,
      MIN_FEFUNC_RGB_INTERCEPT,
      MAX_FEFUNC_RGB_INTERCEPT,
    ),
    b: getValueOnDifferentRangeWithSamePercentage(
      rgb.b,
      MIN_RGB,
      MAX_RGB,
      MIN_FEFUNC_RGB_INTERCEPT,
      MAX_FEFUNC_RGB_INTERCEPT,
    ),
  };

  return feFuncRgbIntercept;
}

/**
 * get white or dark alpha color that have enough contract ratio with the given color
 */
export const getWhiteOrDarkAlphaColor = (color: string, alpha: number = 0.5) => {
  if (colord(color).contrast('#000') > colord(color).contrast('#FFF')) {
    return colord('#000').alpha(alpha).toRgbString();
  }

  return colord('#FFF').alpha(alpha).toRgbString();
};

/**
 * Determines if a given color is grayscale.
 *
 * A color is considered grayscale if its red, green, and blue components are equal,
 * indicating no hue and saturation but only brightness. This function uses the `colord`
 * library to parse and analyze the color. It accepts color in any format supported by `colord`
 * (e.g., HEX, RGB, HSL, etc.) and returns true if the color is grayscale, false otherwise.
 *
 * @param {string} color - The color to be checked, in any format that can be parsed by `colord`.
 * @returns {boolean} True if the color is grayscale, false otherwise.
 */
export const isGrayScale = (color: string): boolean => {
  if (color === '' || !colord(color).isValid()) return false;

  const parsedColor: Colord = colord(color);
  const { r, g, b } = parsedColor.toRgb();
  return r === g && r === b;
};

/**
 * Determines if a given color is black depending on the threshold value.
 * (used for e.g. determining if text is black)
 * @param {string} color
 * @param {number} threshold - The threshold value to determine if the color is black (higher value means lighter color)
 * @returns {boolean} True if the color is black, false otherwise.
 */
export const isColorBlack = (color: string, threshold = 66) => {
  if (color === '' || !colord(color).isValid()) return false;

  const rgb = colord(color).toRgb();
  // Check if all RGB components are below a certain threshold (default 5)
  return rgb.r <= threshold && rgb.g <= threshold && rgb.b <= threshold;
};

/**
 * Determines if a given color is white depending on the threshold value.
 * (used for e.g. determining if text is white)
 * @param {string} color
 * @param {number} threshold - The threshold value to determine if the color is black (higher value means lighter color)
 * @returns {boolean} True if the color is black, false otherwise.
 */
export const isColorWhite = (color: string, threshold = 250) => {
  if (color === '' || !colord(color).isValid()) return false;

  const rgb = colord(color).toRgb();
  // Check if all RGB components are above a certain threshold (default 250)
  return rgb.r >= threshold && rgb.g >= threshold && rgb.b >= threshold;
};

/**
 * Determines if a given color is neutral (white, black, or gray-ish) based on the saturation threshold.
 *
 * @param {string} color - The color to evaluate in any valid color format (e.g., hex, rgb, hsl).
 * @param {number} [saturationThreshold=15] - The saturation threshold to determine if the color is neutral.
 *                                            Colors with saturation below this value are considered neutral.
 * @returns {boolean} True if the color is neutral, false otherwise.
 */
export const isNeutral = (color: string, saturationThreshold: number = 15) => {
  if (color === '' || !colord(color).isValid()) return false;

  const colordColor = colord(color);
  const hsl = colordColor.toHsl();
  // white, black and gray-ish are considered neutral colors
  return hsl.s < saturationThreshold;
};

/**
 * Blends the foreground color proportionaly with the background color depending on how transparent the foreground color is.
 *
 * @param backgroundColor
 * @param foregroundColor
 * @returns
 */
export const blendRgbaColors = (backgroundColor: RgbColor, foregroundColor: RgbaColor) => {
  // If either color is not provided, default to black
  if (!backgroundColor || !foregroundColor) return { r: 0, g: 0, b: 0, a: 1 };

  return {
    r: Math.round((1 - foregroundColor.a) * backgroundColor.r + foregroundColor.a * foregroundColor.r),
    g: Math.round((1 - foregroundColor.a) * backgroundColor.g + foregroundColor.a * foregroundColor.g),
    b: Math.round((1 - foregroundColor.a) * backgroundColor.b + foregroundColor.a * foregroundColor.b),
    a: 1, // The result is always fully opaque when viewed as a composite
  };
};

/**
 * Blends multiple transparent colors proportionally depending on how transparent they are to create a single blended color.
 * The order should be from the bottom-most color to the top-most color.
 *
 * @param colorStrings string[] of colors to blend (can be in any string color format)
 * @returns string hex color of the blended color
 */
export const blendMultipleRgbaColors = (colorStrings: string[]) => {
  // Default to black if no colors are provided
  if (!colorStrings || !colorStrings.length) return '#000000';
  // remove alpha from the color and return it as a hex string
  if (colorStrings.length === 1) return toHex(removeAlpha(colorStrings[0]));

  let compositeColor = parseColorToRgba(colorStrings[0]); // Start with the first color as the base
  for (let i = 1; i < colorStrings.length; i++) {
    const nextColor = parseColorToRgba(colorStrings[i]);
    compositeColor = blendRgbaColors(compositeColor, nextColor);
  }

  // return the blended color as a hex string
  return colord(compositeColor).toHex();
};

/**
 * Generates a complimentary light color by adjusting the alpha value of the original color.
 *
 * @param color - The original color to generate a complimentary light color from.
 * @returns A string representing the complimentary light color.
 */
export const generateComplimentaryLightColor = (color: string) => {
  return colord(color).alpha(0.3).toRgbString();
};
